/**
 * Throughput per site against the capacity actually provisioned in Cato.
 *
 * Cato reports a byte total over the metrics window rather than a rate, so the
 * window length comes back on the summary and the average line rate is derived
 * from it here — a byte total divided by the wrong window is a plausible-looking
 * wrong number, which is worse than no number.
 *
 * Capacity is the sum of each WAN port's provisioned downstream/upstream, so a
 * site whose second link is down shows its real headroom, not its paper one.
 */

import { EmptyState, Meter, MiniSparkline, Typography } from "@norican/react";

import type { NetworkSite } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { averageMbps, formatBytes, formatMbps } from "./status";

/** A site with its throughput resolved into rates and capacity. */
interface SiteBandwidth {
  site: NetworkSite;
  downstreamMbps: number | null;
  upstreamMbps: number | null;
  downstreamCapacity: number;
  upstreamCapacity: number;
}

interface BandwidthPanelProps {
  /** Sites carrying `metrics` from the `accountMetrics` query. */
  sites: NetworkSite[];
  /** Length of the metrics window, in seconds, from the summary. */
  windowSeconds: number | null | undefined;
}

export function BandwidthPanel({ sites, windowSeconds }: BandwidthPanelProps) {
  const measured = resolve(sites, windowSeconds);

  return (
    <PanelCard
      title="Bandwidth per site"
      caption={
        windowSeconds ? `Average over the last ${Math.round(windowSeconds / 60)} min` : undefined
      }
    >
      {measured.length === 0 ? (
        <EmptyState
          size="sm"
          title="No throughput data"
          description="Cato's accountMetrics query returned nothing for this window. The site table above is unaffected."
        />
      ) : (
        <div className="flex flex-col gap-6">
          {measured.map(
            ({ site, downstreamMbps, upstreamMbps, downstreamCapacity, upstreamCapacity }) => (
              <div key={site.id} className="flex flex-col gap-2">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                  <Typography variant="body-sm">{site.name}</Typography>
                  <Typography variant="caption" tone="muted" tabular>
                    {formatBytes(site.metrics?.bytes_total)} moved · {formatMbps(downstreamMbps)}{" "}
                    down · {formatMbps(upstreamMbps)} up
                  </Typography>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
                  <div className="flex flex-col gap-2">
                    <Rate
                      label="Downstream"
                      value={downstreamMbps}
                      capacity={downstreamCapacity}
                      siteName={site.name}
                    />
                    <Rate
                      label="Upstream"
                      value={upstreamMbps}
                      capacity={upstreamCapacity}
                      siteName={site.name}
                    />
                  </div>

                  {(site.metrics?.downstream_trend?.length ?? 0) > 1 && (
                    <div className="flex items-center justify-end">
                      <MiniSparkline
                        data={site.metrics?.downstream_trend ?? []}
                        tone="accent"
                        endMarker="dot"
                        width={120}
                        height={28}
                        aria-label={`Downstream throughput trend for ${site.name}`}
                      />
                    </div>
                  )}
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </PanelCard>
  );
}

interface RateProps {
  /** Direction label. */
  label: string;
  /** Observed average rate in Mbps, or null when unmeasured. */
  value: number | null;
  /** Provisioned capacity in Mbps. Zero when Cato has none configured. */
  capacity: number;
  /** Owning site, for the accessible name. */
  siteName: string;
}

/** Utilisation thresholds — a link this busy is worth looking at. */
const BUSY_PCT = 70;
const SATURATED_PCT = 90;

function Rate({ label, value, capacity, siteName }: RateProps) {
  if (value == null) {
    return (
      <Typography variant="caption" tone="muted">
        {label}: no measurement in this window
      </Typography>
    );
  }

  // Without a provisioned figure there is no scale to measure against, so show
  // the rate rather than a meter filled against an invented ceiling.
  if (capacity <= 0) {
    return (
      <Typography variant="caption" tone="muted" tabular>
        {label}: {formatMbps(value)} — no provisioned capacity configured in Cato
      </Typography>
    );
  }

  const pct = (value / capacity) * 100;
  const tone = pct >= SATURATED_PCT ? "danger" : pct >= BUSY_PCT ? "warning" : "accent";

  return (
    <Meter.Root
      value={Math.min(value, capacity)}
      min={0}
      max={capacity}
      size="sm"
      tone={tone}
      aria-valuetext={`${label} ${formatMbps(value)} of ${formatMbps(capacity)} for ${siteName}`}
    >
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <Meter.Label>
          <Typography variant="caption" tone="muted">
            {label}
          </Typography>
        </Meter.Label>
        <Meter.Value>
          {() => (
            <Typography variant="caption" tone="muted" tabular>
              {formatMbps(value)} / {formatMbps(capacity)} ({pct.toFixed(0)}%)
            </Typography>
          )}
        </Meter.Value>
      </div>
      <Meter.Track>
        <Meter.Indicator />
      </Meter.Track>
    </Meter.Root>
  );
}

/** Resolve each site's byte totals into rates, busiest first. */
function resolve(sites: NetworkSite[], windowSeconds: number | null | undefined): SiteBandwidth[] {
  const seconds = windowSeconds ?? 0;
  return sites
    .filter((site) => site.metrics != null)
    .map((site) => ({
      site,
      downstreamMbps: averageMbps(site.metrics?.bytes_downstream, seconds),
      upstreamMbps: averageMbps(site.metrics?.bytes_upstream, seconds),
      downstreamCapacity: sumCapacity(site, "downstream_bandwidth_mbps"),
      upstreamCapacity: sumCapacity(site, "upstream_bandwidth_mbps"),
    }))
    .sort((a, b) => (b.downstreamMbps ?? 0) - (a.downstreamMbps ?? 0));
}

/** Sum one direction's provisioned capacity across a site's WAN ports. */
function sumCapacity(
  site: NetworkSite,
  field: "downstream_bandwidth_mbps" | "upstream_bandwidth_mbps",
): number {
  return (site.links ?? []).reduce((total, link) => total + (link[field] ?? 0), 0);
}
